/**
 * FormElement の編集 UI（質問 + レイアウト要素、@dnd-kit で並べ替え対応）。
 * 仕様書: docs/01_spec/spec_line_bot.md A-12.4（Phase 24b 拡張）
 */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Heading,
  Image as ImageIcon,
  Minus,
  Plus,
  Text,
  Trash2,
  Type,
  type LucideIcon,
} from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';

import type {
  FieldFormElement,
  FormElement,
  FormFieldType,
} from '@goenhub/shared';

import { ImageGalleryDialog } from '@/components/common/ImageGalleryDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Props {
  elements: FormElement[];
  onChange: (elements: FormElement[]) => void;
}

function genId(): string {
  return `e_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

const FIELD_TYPE_LABEL: Record<FormFieldType, string> = {
  text: 'text（1 行）',
  textarea: 'textarea（複数行）',
  email: 'email（メアド検証）',
  // 顧客標準フィールド (2026-06-14) — 送信時にオーディエンスの正規枠へ自動登録される
  last_name: '姓（顧客名・自動登録）',
  first_name: '名（顧客名・自動登録）',
  last_name_kana: 'せい（フリガナ・自動登録）',
  first_name_kana: 'めい（フリガナ・自動登録）',
  phone: '電話番号（顧客項目・自動登録）',
  company_name: '会社名（顧客項目・自動登録）',
  birthdate: '生年月日（顧客項目・自動登録）',
  tel: 'tel（電話番号・自由記述）',
  number: 'number（数値）',
  select: 'select（選択肢）',
  radio: 'radio（単一選択）',
  checkbox: 'checkbox（複数選択）',
  date: 'date（日付）',
  consent: 'consent（同意チェック）',
  reply_request: '返答要求（チェックボックス）',
  billing_address: '請求先住所（4 項目セット）',
  delivery_address: '納品先住所（4 項目セット）',
  english_profile: '英表記登録情報（英語名・電話・住所）',
  briefing_schedule_picker: '個別説明 候補日（動的）',
  product_picker: '製品（動的、見積）',
  assignee_picker: '担当者（動的）',
};

const DYNAMIC_FIELD_HINTS: Partial<Record<FormFieldType, string>> = {
  last_name:
    '送信時にメール配信先（オーディエンス）の「姓」へ自動登録。姓・名が揃うと表示名も自動生成。カスタムフィールドには入りません。',
  first_name:
    '送信時にメール配信先（オーディエンス）の「名」へ自動登録。姓・名が揃うと表示名も自動生成。カスタムフィールドには入りません。',
  last_name_kana:
    '送信時にメール配信先の「せい（フリガナ）」へ自動登録。カスタムフィールドには入りません。',
  first_name_kana:
    '送信時にメール配信先の「めい（フリガナ）」へ自動登録。カスタムフィールドには入りません。',
  phone:
    '送信時にメール配信先の「電話番号」へ自動登録。カスタムフィールドには入りません（自由記述だけ残したい場合は tel を使用）。',
  company_name:
    '送信時にメール配信先の「会社名」へ自動登録。重複判定用の正規化名も同時更新されます（「株式会社○○」と「(株)○○」は同じ会社として扱われます）。カスタムフィールドには入りません。',
  birthdate:
    '送信時にメール配信先の「生年月日」(西暦 YYYY-MM-DD) へ自動登録。日付形式に正規化できない回答は反映されません。カスタムフィールドには入りません。',
  reply_request:
    'チェックして送信された回答は、回収一覧に「返答待ち」ラベルが付きます。回収一覧のメールボタンから返信メールを送ると「返答済み」に変わります（ラベルは回収一覧のプルダウンで手動変更も可能）。あわせて掲示板「お問合せ対応」カテゴリーへ作業依頼（緊急度=中／期日=4日後）を自動投稿し、営業部署の社員へ通知します（18:00 以降の受付は翌朝 9:30 公開）。',
  briefing_schedule_picker:
    '公開フォーム表示時に briefing_schedules から isActive=true かつ受付可能な枠を動的取得。回答値は scheduleId の配列。',
  product_picker:
    '公開フォーム表示時に products_list の有効期間内 (Form.primaryCategoryId 設定時はそれで絞込) から動的取得。回答値は選択された製品の配列。',
  assignee_picker:
    '公開フォーム表示時に admins (editor 以上) から動的取得。回答値は担当者の uid。',
  billing_address:
    '郵便番号 / 都道府県 / 市区町村 / 町名・番地・建物の 4 項目を 1 セットで入力。送信時に顧客の請求先住所へ反映されます（同じ住所なら更新、違う住所なら追加して「既定」に切替。以前の住所は残ります）。',
  delivery_address:
    '郵便番号 / 都道府県 / 市区町村 / 町名・番地・建物の 4 項目を 1 セットで入力。送信時に顧客の納品先住所へ反映されます（同じ住所なら更新、違う住所なら追加して「既定」に切替。以前の住所は残ります）。',
  english_profile:
    '英語名 / 国番号付き電話 / 国 / 英語住所（国別フィールド）を 1 セットで入力。送信時に顧客の英語名 / 国際電話番号 / 英文住所へ反映されます（単数項目のため、上書きするかは設定タブ「顧客情報の更新」に従います）。',
};

/** 「要素追加」メニュー / 挿入モーダルで共用する要素種別の一覧。 */
const ELEMENT_KIND_OPTIONS = [
  {
    kind: 'field',
    label: '質問フィールド',
    icon: Type,
    desc: 'text / select 等',
  },
  { kind: 'title', label: 'タイトル', icon: Heading, desc: 'H1〜H3 見出し' },
  { kind: 'body', label: '本文段落', icon: Text, desc: '説明テキスト' },
  { kind: 'image', label: '画像', icon: ImageIcon, desc: '画像セクション' },
  { kind: 'divider', label: '区切り線', icon: Minus, desc: '視覚的な区切り' },
] as const satisfies ReadonlyArray<{
  kind: FormElement['kind'];
  label: string;
  icon: LucideIcon;
  desc: string;
}>;

/** ホバー継続で挿入 UI を出すまでの待ち時間 (ms)。誤爆防止のため即時には出さない。 */
const INSERT_GAP_HOVER_MS = 1500;

/**
 * 要素カードの間に挟む「ここに挿入」ゾーン (2026-07-28)。
 *
 * 1.5 秒ホバーし続けると隙間が広がり「+」が出る。クリックで親のモーダルを開く。
 * 既定は space-y-2 相当の高さ (8px) に畳んであり、通常の見た目は変わらない。
 * ドラッグ並べ替え中 (disabled) は反応しない。
 */
function InsertGap({
  onOpen,
  disabled,
}: {
  onOpen: () => void;
  disabled: boolean;
}) {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  // アンマウント時にタイマーを残さない
  useEffect(() => clear, []);

  if (disabled) return <div className="h-2" />;

  return (
    <div
      onMouseEnter={() => {
        clear();
        timerRef.current = setTimeout(() => setArmed(true), INSERT_GAP_HOVER_MS);
      }}
      onMouseLeave={() => {
        clear();
        setArmed(false);
      }}
      className={`relative flex items-center justify-center transition-all duration-200 ${
        armed ? 'h-10' : 'h-2'
      }`}
    >
      {armed && (
        <>
          <div className="absolute inset-x-0 top-1/2 h-px border-t border-dashed border-[var(--accent)] opacity-50" />
          <button
            type="button"
            onClick={onOpen}
            title="ここに要素を挿入"
            aria-label="ここに要素を挿入"
            className="relative z-10 flex items-center justify-center size-6 rounded-full border border-[var(--accent)] bg-[var(--bg-surface)] text-[var(--accent)] shadow-sm hover:bg-[var(--accent)] hover:text-white transition-colors"
          >
            <Plus className="size-3.5" />
          </button>
        </>
      )}
    </div>
  );
}

function SortableElementRow({
  element,
  onUpdate,
  onRemove,
}: {
  element: FormElement;
  onUpdate: (next: FormElement) => void;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: element.id });
  const [galleryOpen, setGalleryOpen] = useState(false);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-3"
    >
      <div className="flex items-start gap-2">
        <button
          ref={setActivatorNodeRef}
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-[var(--text-muted)] pt-2 hover:text-[var(--accent)]"
          aria-label="並べ替え"
        >
          <GripVertical className="size-4" />
        </button>
        <div className="flex-1 space-y-2">
          {/* === 要素別フォーム === */}
          {element.kind === 'field' && (
            <FieldElementForm element={element} onUpdate={onUpdate} />
          )}
          {element.kind === 'title' && (
            <TitleElementForm element={element} onUpdate={onUpdate} />
          )}
          {element.kind === 'body' && (
            <BodyElementForm element={element} onUpdate={onUpdate} />
          )}
          {element.kind === 'image' && (
            <ImageElementForm
              element={element}
              onUpdate={onUpdate}
              onPickImage={() => setGalleryOpen(true)}
            />
          )}
          {element.kind === 'divider' && (
            <DividerElementForm />
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          title="要素を削除"
        >
          <Trash2 />
        </Button>
      </div>

      {element.kind === 'image' && (
        <ImageGalleryDialog
          open={galleryOpen}
          onOpenChange={setGalleryOpen}
          title="フォーム画像を選択"
          onSelect={(url) => {
            onUpdate({ ...element, imageUrl: url });
          }}
        />
      )}
    </div>
  );
}

/**
 * 選択上限数の入力テキスト → FieldFormElement.maxSelections。
 * 空欄 / 数値でない / 1 未満 は「上限なし」= undefined。
 */
function parseMaxSelections(text: string): number | undefined {
  const n = Number.parseInt(text.trim(), 10);
  return Number.isFinite(n) && n >= 1 ? n : undefined;
}

// ─────────────────────────────────────────────
// 要素別フォーム
// ─────────────────────────────────────────────
function FieldElementForm({
  element,
  onUpdate,
}: {
  element: FieldFormElement;
  onUpdate: (next: FormElement) => void;
}) {
  const usesOptions =
    element.fieldType === 'select' ||
    element.fieldType === 'radio' ||
    element.fieldType === 'checkbox';

  // 選択肢テキストの編集中バッファ。
  // 確定 options（trim + 空行除去）を直接 value にすると、行末で改行しても
  // 即座に空行が削られてカーソルが次行へ移れない。生テキストを保持して回避する。
  const [optionsText, setOptionsText] = useState(() =>
    (element.options ?? []).join('\n'),
  );
  // 選択上限数の編集中バッファ。空欄 / 0 は「上限なし」= undefined に落とすが、
  // 入力欄の表示は打った文字のまま残す（打鍵ごとに勝手に消えないように）。
  const [maxSelectionsText, setMaxSelectionsText] = useState(() =>
    typeof element.maxSelections === 'number'
      ? String(element.maxSelections)
      : '',
  );
  useEffect(() => {
    // 外部から options が変わった場合のみ再同期（自分の編集では上書きしない）
    const external = (element.options ?? []).join('\n');
    const localCleaned = optionsText
      .split('\n')
      .map((o) => o.trim())
      .filter(Boolean)
      .join('\n');
    if (external !== localCleaned) {
      setOptionsText(external);
    }
    // optionsText は意図的に依存から除外（element.options の変化にのみ反応させる）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.options]);

  useEffect(() => {
    // 外部から maxSelections が変わった場合のみ再同期。
    // 自分の入力（"0" や "abc" のような未確定値）では上書きしない。
    if (parseMaxSelections(maxSelectionsText) !== element.maxSelections) {
      setMaxSelectionsText(
        typeof element.maxSelections === 'number'
          ? String(element.maxSelections)
          : '',
      );
    }
    // maxSelectionsText は意図的に依存から除外
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.maxSelections]);

  const optionCount = element.options?.length ?? 0;
  const maxSelectionsOverflow =
    typeof element.maxSelections === 'number' &&
    optionCount > 0 &&
    element.maxSelections > optionCount;

  return (
    <>
      <div className="flex items-center gap-2 text-[10px] text-[var(--text-muted)]">
        <Type className="size-3" />
        質問フィールド
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">ラベル</Label>
          <Input
            value={element.label}
            onChange={(e) => onUpdate({ ...element, label: e.target.value })}
            placeholder="例: お名前"
          />
        </div>
        <div>
          <Label className="text-xs">入力タイプ</Label>
          <Select
            value={element.fieldType}
            onValueChange={(v) =>
              onUpdate({
                ...element,
                fieldType: v as FormFieldType,
                options:
                  v === 'select' || v === 'radio' || v === 'checkbox'
                    ? element.options ?? []
                    : [],
                // 選択上限は checkbox 専用。他の型へ変えたら残骸を消す。
                maxSelections:
                  v === 'checkbox' ? element.maxSelections : undefined,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(FIELD_TYPE_LABEL) as Array<[FormFieldType, string]>).map(
                ([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">プレースホルダ</Label>
          <Input
            value={element.placeholder ?? ''}
            onChange={(e) => onUpdate({ ...element, placeholder: e.target.value })}
            placeholder="例: 山田 太郎"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">補足説明（任意）</Label>
          <Input
            value={element.helpText ?? ''}
            onChange={(e) => onUpdate({ ...element, helpText: e.target.value })}
            placeholder="フィールド下に表示"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 px-3 py-2 rounded border border-[var(--border)] bg-[var(--bg-elevated)] cursor-pointer flex-1">
            <input
              type="checkbox"
              checked={element.required}
              onChange={(e) => onUpdate({ ...element, required: e.target.checked })}
              className="size-3.5"
            />
            <span className="text-xs">必須</span>
          </label>
        </div>
      </div>
      {element.fieldType === 'consent' && (
        <div>
          <Label className="text-xs">同意文</Label>
          <Input
            value={element.consentText ?? ''}
            onChange={(e) => onUpdate({ ...element, consentText: e.target.value })}
            placeholder="例: 利用規約に同意します"
          />
        </div>
      )}
      {usesOptions && (
        <div>
          <Label className="text-xs">選択肢（改行区切り）</Label>
          <textarea
            value={optionsText}
            onChange={(e) => {
              setOptionsText(e.target.value);
              onUpdate({
                ...element,
                options: e.target.value
                  .split('\n')
                  .map((o) => o.trim())
                  .filter(Boolean),
              });
            }}
            className="w-full min-h-[60px] rounded border border-[var(--border)] bg-[var(--bg-input)] p-2 text-[13px]"
            placeholder="選択肢A&#10;選択肢B&#10;選択肢C"
          />
        </div>
      )}
      {element.fieldType === 'checkbox' && (
        <div>
          <Label className="text-xs">選択上限数（任意）</Label>
          <Input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            className="w-40"
            value={maxSelectionsText}
            onChange={(e) => {
              setMaxSelectionsText(e.target.value);
              onUpdate({
                ...element,
                maxSelections: parseMaxSelections(e.target.value),
              });
            }}
            placeholder="空欄なら上限なし"
          />
          <p className="text-[10px] text-[var(--text-muted)] mt-1 leading-relaxed">
            公開フォームでは「選択数 / 上限」を表示し、上限に達すると残りの選択肢を
            選べなくします。空欄なら上限なし。
            {element.required
              ? '「必須」がオンのため、上限数ちょうど選ばないと送信できません。'
              : '「必須」がオフのため、上限に達していなくても送信できます。'}
          </p>
          {maxSelectionsOverflow && (
            <p className="text-[10px] text-amber-600 mt-1">
              選択肢が {optionCount} つしかないため、実際の上限は {optionCount}{' '}
              つとして扱われます。
            </p>
          )}
        </div>
      )}
      {element.fieldType === 'briefing_schedule_picker' && (
        <div>
          <Label className="text-xs">参加スタイル絞込（任意）</Label>
          <Select
            value={element.filterStyle ?? '__any__'}
            onValueChange={(v) =>
              onUpdate({
                ...element,
                filterStyle:
                  v === '__any__' ? undefined : (v as 'online' | 'onsite' | 'both'),
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">全て (絞込なし)</SelectItem>
              <SelectItem value="online">オンライン</SelectItem>
              <SelectItem value="onsite">現地</SelectItem>
              <SelectItem value="both">オンライン or 現地</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {DYNAMIC_FIELD_HINTS[element.fieldType] && (
        <div className="rounded-sm border border-[var(--accent-subtle)] bg-[var(--accent-subtle)] px-3 py-2 text-xs text-[var(--accent)] leading-relaxed">
          {DYNAMIC_FIELD_HINTS[element.fieldType]}
        </div>
      )}
    </>
  );
}

function TitleElementForm({
  element,
  onUpdate,
}: {
  element: Extract<FormElement, { kind: 'title' }>;
  onUpdate: (next: FormElement) => void;
}) {
  return (
    <>
      <div className="flex items-center gap-2 text-[10px] text-[var(--text-muted)]">
        <Heading className="size-3" />
        タイトル見出し
      </div>
      <div className="grid grid-cols-[1fr_120px] gap-2">
        <div>
          <Label className="text-xs">テキスト</Label>
          <Input
            value={element.text}
            onChange={(e) => onUpdate({ ...element, text: e.target.value })}
            placeholder="例: ご来場予約フォーム"
          />
        </div>
        <div>
          <Label className="text-xs">レベル</Label>
          <Select
            value={element.level}
            onValueChange={(v) =>
              onUpdate({ ...element, level: v as 'h1' | 'h2' | 'h3' })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="h1">H1（最大）</SelectItem>
              <SelectItem value="h2">H2</SelectItem>
              <SelectItem value="h3">H3（小見出し）</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
}

function BodyElementForm({
  element,
  onUpdate,
}: {
  element: Extract<FormElement, { kind: 'body' }>;
  onUpdate: (next: FormElement) => void;
}) {
  return (
    <>
      <div className="flex items-center gap-2 text-[10px] text-[var(--text-muted)]">
        <Text className="size-3" />
        本文段落
      </div>
      <Label className="text-xs">テキスト</Label>
      <textarea
        value={element.text}
        onChange={(e) => onUpdate({ ...element, text: e.target.value })}
        rows={3}
        className="w-full rounded border border-[var(--border)] bg-[var(--bg-input)] p-2 text-[13px]"
        placeholder="フォームの説明文や注意事項などを記載"
      />
    </>
  );
}

function ImageElementForm({
  element,
  onUpdate,
  onPickImage,
}: {
  element: Extract<FormElement, { kind: 'image' }>;
  onUpdate: (next: FormElement) => void;
  onPickImage: () => void;
}) {
  const imgRef = useRef<HTMLImageElement>(null);

  // 画像 URL 変更時にサイズを取得して保存
  useEffect(() => {
    if (!element.imageUrl) return;
    const img = new window.Image();
    img.onload = () => {
      if (
        element.width !== img.naturalWidth ||
        element.height !== img.naturalHeight
      ) {
        onUpdate({
          ...element,
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    };
    img.src = element.imageUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.imageUrl]);

  return (
    <>
      <div className="flex items-center gap-2 text-[10px] text-[var(--text-muted)]">
        <ImageIcon className="size-3" />
        画像セクション
      </div>
      {element.imageUrl ? (
        <div className="space-y-2">
          <img
            ref={imgRef}
            src={element.imageUrl}
            alt={element.alt ?? ''}
            className="max-w-full max-h-48 rounded border border-[var(--border)]"
          />
          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={onPickImage}>
              画像を変更
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onUpdate({ ...element, imageUrl: '' })}
            >
              画像を削除
            </Button>
            <span className="text-[10px] text-[var(--text-muted)] ml-auto">
              {element.width ?? '?'} × {element.height ?? '?'}
            </span>
          </div>
        </div>
      ) : (
        <Button type="button" variant="primary" size="sm" onClick={onPickImage}>
          <ImageIcon className="size-3.5 mr-1" />
          画像を選択
        </Button>
      )}
      <div>
        <Label className="text-xs">代替テキスト（alt、任意）</Label>
        <Input
          value={element.alt ?? ''}
          onChange={(e) => onUpdate({ ...element, alt: e.target.value })}
          placeholder="例: イベント告知バナー"
        />
      </div>
    </>
  );
}

function DividerElementForm() {
  return (
    <>
      <div className="flex items-center gap-2 text-[10px] text-[var(--text-muted)]">
        <Minus className="size-3" />
        区切り線
      </div>
      <div className="text-xs text-[var(--text-muted)] italic">
        セクション間の視覚的な区切りとして表示されます。
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
// メイン
// ─────────────────────────────────────────────
export function FormElementEditor({ elements: rawElements, onChange }: Props) {
  // 旧データや undefined への防御
  const elements = Array.isArray(rawElements) ? rawElements : [];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  /** 挿入モーダルを開いている位置 (この index の直前に挿入)。null = 閉じている。 */
  const [insertAt, setInsertAt] = useState<number | null>(null);
  /** 並べ替えドラッグ中は挿入ゾーンを反応させない。 */
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleDragEnd = (event: DragEndEvent) => {
    setDragging(false);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = elements.findIndex((e) => e.id === active.id);
    const newIdx = elements.findIndex((e) => e.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onChange(arrayMove(elements, oldIdx, newIdx));
  };

  const updateElement = (idx: number, next: FormElement) => {
    onChange(elements.map((e, i) => (i === idx ? next : e)));
  };
  const removeElement = (idx: number) => {
    onChange(elements.filter((_, i) => i !== idx));
  };

  /**
   * 要素を追加する。at を渡すとその index の直前に挿入、省略時は末尾に追加。
   */
  const addElement = (kind: FormElement['kind'], at?: number) => {
    const id = genId();
    let newEl: FormElement;
    switch (kind) {
      case 'field':
        newEl = {
          id,
          kind: 'field',
          fieldType: 'text',
          label: '',
          required: false,
          options: [],
        };
        break;
      case 'title':
        newEl = { id, kind: 'title', text: '', level: 'h2' };
        break;
      case 'body':
        newEl = { id, kind: 'body', text: '' };
        break;
      case 'image':
        newEl = { id, kind: 'image', imageUrl: '' };
        break;
      case 'divider':
        newEl = { id, kind: 'divider' };
        break;
    }
    if (typeof at === 'number') {
      const next = [...elements];
      next.splice(at, 0, newEl);
      onChange(next);
    } else {
      onChange([...elements, newEl]);
    }
    setMenuOpen(false);
    setInsertAt(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Label className="mb-0">質問・表示要素</Label>
          <p className="text-[10px] text-[var(--text-muted)] mt-0.5 leading-relaxed">
            上から順に公開ページへ表示されます。左端の
            <GripVertical className="inline size-3 -mt-0.5" />
            で並べ替え、カードとカードの間にカーソルを置くと途中に挿入できます。
          </p>
        </div>
        <div ref={menuRef} className="relative flex-shrink-0">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <Plus />
            要素追加
          </Button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 z-20 rounded border border-[var(--border)] bg-[var(--bg-surface)] shadow-lg overflow-hidden min-w-[200px]">
              {ELEMENT_KIND_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.kind}
                    type="button"
                    onClick={() => addElement(opt.kind)}
                    className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-[var(--bg-hover)]"
                  >
                    <Icon className="size-4 mt-0.5 text-[var(--text-muted)]" />
                    <div className="flex-1">
                      <div className="text-[13px] font-medium">{opt.label}</div>
                      <div className="text-[10px] text-[var(--text-muted)]">
                        {opt.desc}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {elements.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)] italic py-4 text-center">
          要素が未登録です
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={() => setDragging(true)}
          onDragCancel={() => setDragging(false)}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={elements.map((e) => e.id)}
            strategy={verticalListSortingStrategy}
          >
            {/* space-y-2 は使わず、カード間の隙間は InsertGap (畳んだ状態で 8px) が担う */}
            <div className="flex flex-col">
              {elements.map((el, idx) => (
                <Fragment key={el.id}>
                  {idx > 0 && (
                    <InsertGap
                      disabled={dragging}
                      onOpen={() => setInsertAt(idx)}
                    />
                  )}
                  <SortableElementRow
                    element={el}
                    onUpdate={(next) => updateElement(idx, next)}
                    onRemove={() => removeElement(idx)}
                  />
                </Fragment>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* カード間の「+」から開く要素挿入モーダル */}
      <Dialog
        open={insertAt !== null}
        onOpenChange={(open) => {
          if (!open) setInsertAt(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {insertAt !== null
                ? `${insertAt} 番目と ${insertAt + 1} 番目の間に挿入`
                : '要素を挿入'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            {ELEMENT_KIND_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.kind}
                  type="button"
                  onClick={() => {
                    if (insertAt !== null) addElement(opt.kind, insertAt);
                  }}
                  className="w-full flex items-start gap-2 px-3 py-2 text-left rounded border border-[var(--border)] hover:border-[var(--accent)] hover:bg-[var(--bg-hover)] transition-colors"
                >
                  <Icon className="size-4 mt-0.5 text-[var(--text-muted)]" />
                  <div className="flex-1">
                    <div className="text-[13px] font-medium">{opt.label}</div>
                    <div className="text-[10px] text-[var(--text-muted)]">
                      {opt.desc}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
